import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import '../styles/Bilheteria.css';
import { FaTicketAlt, FaCalendarAlt, FaMapMarkerAlt, FaInfoCircle, FaCheckCircle, FaUser, FaClock } from 'react-icons/fa';
import API_BASE_URL from '../config/api';

const STRIPE_PK = 'pk_live_51TGcfPKmhnqdWeH5Q4hXyWUSydkJdPMJwDd4PR1DsAW2hSlDYJmstsZLprRCyUIxd6a0j3uLLTDNhkMfC1BEw3Fk002NxTskSi'
const stripePromise = loadStripe(STRIPE_PK);

const Bilheteria = () => {
    const navigate = useNavigate();
    const [selectedModalidade, setSelectedModalidade] = useState('Todas');
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [showPurchaseModal, setShowPurchaseModal] = useState(false);
    const [purchaseStep, setPurchaseStep] = useState('summary'); // 'summary' | 'processing' | 'success' | 'pending'
    const [isSocio, setIsSocio] = useState(false);
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [quantity, setQuantity] = useState(1);
    const [showAnnualInfoModal, setShowAnnualInfoModal] = useState(false);
    const [isAnnualTicketPurchase, setIsAnnualTicketPurchase] = useState(false);
    const [allSports, setAllSports] = useState([]);
    const [selectedAnnualSports, setSelectedAnnualSports] = useState([]);
    const [selectedProfiles, setSelectedProfiles] = useState([]);
    const [linkedUsers, setLinkedUsers] = useState(() => {
        try {
            const stored = localStorage.getItem('linkedUsers');
            const parsed = stored ? JSON.parse(stored) : [];
            console.log('Linked Users Debug:', parsed);
            return parsed;
        } catch { return []; }
    });

    // Refresh linked users from server on mount
    useEffect(() => {
        const refreshLinkedUsers = async () => {
            try {
                const token = localStorage.getItem('token');
                if (!token) return;
                const res = await fetch(`${API_BASE_URL}/api/auth/linked-users`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (res.ok) {
                    const data = await res.json();
                    console.log('Refreshed Linked Users:', data);
                    setLinkedUsers(data);
                    if (data.length > 0) {
                        localStorage.setItem('linkedUsers', JSON.stringify(data));
                    }
                }
            } catch (err) {
                console.warn('Could not refresh linked users in Bilheteria:', err);
            }
        };
        refreshLinkedUsers();
    }, []);

    useEffect(() => {
        // Check for success/canceled query params from Stripe
        const query = new URLSearchParams(window.location.search);
        if (query.get('success')) {
            const eventId = query.get('eventId');
            // Stripe includes payment_intent and payment_intent_client_secret on redirect
            const paymentIntentId = query.get('payment_intent');
            const clientSecret = query.get('payment_intent_client_secret');

            const setEventContext = () => {
                if (eventId === 'annual-ticket') {
                    setIsAnnualTicketPurchase(true);
                    setSelectedEvent({
                        id: 'annual-ticket',
                        homeTeam: 'Bilhete Anual',
                        awayTeam: '2025/2026',
                        date: 'Época 2025/2026',
                        time: 'Todos os jogos',
                        location: 'Pavilhão Clube Desportivo da Póvoa',
                        ticketPriceSocio: 40,
                        ticketPriceNonSocio: 40,
                        isAnnual: true
                    });
                }
            };

            setEventContext();
            setShowPurchaseModal(true);

            // If Stripe provided a client secret, verify the actual payment status
            // before showing success — needed for async methods (MBWay, Multibanco)
            if (clientSecret) {
                stripePromise.then(stripe => {
                    if (!stripe) return;
                    stripe.retrievePaymentIntent(clientSecret).then(({ paymentIntent }) => {
                        if (paymentIntent?.status === 'succeeded') {
                            setPurchaseStep('success');
                        } else {
                            // Payment is pending (e.g. MBWay awaiting user approval) or failed
                            setPurchaseStep('pending');
                        }
                    }).catch(() => {
                        // Fallback: if we can't verify, show pending to be safe
                        setPurchaseStep('pending');
                    });
                });
            } else {
                // No client secret in URL (e.g. old Checkout Session flow): show success
                setPurchaseStep('success');
            }

            window.history.replaceState({}, document.title, window.location.pathname);
        }
        if (query.get('canceled')) {
            alert('O pagamento foi cancelado.');
            window.history.replaceState({}, document.title, window.location.pathname);
        }

        const fetchPublicEvents = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/api/events/public`);
                if (response.ok) {
                    const data = await response.json();

                    // Formatar dados para a interface
                    const formattedEvents = data
                        .filter(e => e.eventType !== 1 || e.isHomeGame)
                        .map(e => {
                            const dateObj = new Date(e.startDateTime);

                            const homeTeam = e.isHomeGame ? 'CD Póvoa' : (e.opponentName || 'Adversário');
                            const awayTeam = e.isHomeGame ? (e.opponentName || 'Adversário') : 'CD Póvoa';

                            return {
                                ...e, // Keep original data for modal
                                id: e.id,
                                homeTeam,
                                awayTeam,
                                date: dateObj.toLocaleDateString('pt-PT', { day: 'numeric', month: 'long', year: 'numeric' }),
                                time: dateObj.toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' }),
                                competition: e.title || `Jogo ${e.sportName}`,
                                modalidade: e.sportName,
                                location: e.location || 'Pavilhão Clube Desportivo da Póvoa',
                                status: 'available'
                            };
                        });

                    setEvents(formattedEvents);
                }
            } catch (error) {
                console.error('Error fetching public events:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchPublicEvents();

        // Check auth status
        const token = localStorage.getItem('token');
        setIsLoggedIn(!!token);

        // Check if user is socio
        const roles = localStorage.getItem('roles');
        if (roles) {
            try {
                const rolesArray = JSON.parse(roles);
                console.log(rolesArray);
                const hasSocioRole = rolesArray.some(role =>
                    role.toLowerCase() === 'socio' || role.toLowerCase() === 'admin' || role.toLowerCase() === 'atleta'
                );
                setIsSocio(hasSocioRole);
            } catch (e) {
                console.error('Error parsing roles', e);
            }
        }
        const fetchSports = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/api/sports`);
                if (response.ok) {
                    const data = await response.json();
                    console.log(data);
                    setAllSports(data);
                }
            } catch (error) {
                console.error('Error fetching sports:', error);
            }
        };

        fetchSports();
    }, [isLoggedIn]);

    const handleBuyClick = (event) => {
        const token = localStorage.getItem('token');
        if (!token) {
            navigate('/login');
            return;
        }

        setSelectedEvent(event);
        setIsAnnualTicketPurchase(false);

        if (linkedUsers.length > 1) {
            setPurchaseStep('selection');
            setSelectedProfiles([]);
            setQuantity(0);
        } else {
            setPurchaseStep('summary');
            const defaultProfile = linkedUsers[0] || null;
            setSelectedProfiles(defaultProfile ? [defaultProfile] : []);
            setQuantity(1);
        }
        setShowPurchaseModal(true);
    };

    const handleAnnualTicketBuy = () => {
        const token = localStorage.getItem('token');
        if (!token) {
            navigate('/login');
            return;
        }

        setIsAnnualTicketPurchase(true);
        setSelectedEvent({
            id: 'annual-ticket',
            homeTeam: 'Bilhete Anual',
            awayTeam: '2025/2026',
            date: 'Época 2025/2026',
            time: 'Todos os jogos',
            location: 'Pavilhão Clube Desportivo da Póvoa',
            ticketPriceSocio: 40,
            ticketPriceNonSocio: 40,
            isAnnual: true
        });
        setShowAnnualInfoModal(false);
        setSelectedAnnualSports([]); // Reset sports selection

        setPurchaseStep('sport-selection');
        setShowPurchaseModal(true);
    };

    const confirmPurchase = async () => {
        if (linkedUsers.length > 1 && selectedProfiles.length === 0) {
            alert('Por favor, seleciona pelo menos um perfil para continuar.');
            return;
        }

        const userEmail = localStorage.getItem('userEmail') || '';
        const userName = localStorage.getItem('userName') || '';
        const totalAmount = calculateTotal();
        const buyerUserId = localStorage.getItem('userId');

        // Build the profiles list for the checkout
        let profilesToCharge = [];

        if (isAnnualTicketPurchase) {
            selectedProfiles.forEach(profile => {
                selectedAnnualSports.forEach(sport => {
                    const price = getTicketPrice(selectedEvent, profile, sport);
                    profilesToCharge.push({
                        id: profile.id,
                        name: `${profile.firstName} ${profile.lastName}`,
                        email: profile.email || userEmail,
                        price,
                        sportId: sport.id
                    });
                });
            });
        } else {
            profilesToCharge = selectedProfiles.map(p => ({
                id: p.id,
                name: `${p.firstName} ${p.lastName}`,
                email: p.email || userEmail,
                price: getTicketPrice(selectedEvent, p),
                sportId: null
            }));
        }

        // Navigate to the custom checkout page with context
        navigate('/checkout', {
            state: {
                type: 'ticket',
                amount: totalAmount,
                description: isAnnualTicketPurchase
                    ? `Bilhete Anual 2025/2026 — ${profilesToCharge.map(p => p.name).join(', ')}`
                    : `Bilhete: ${selectedEvent.homeTeam} vs ${selectedEvent.awayTeam}`,
                eventId: selectedEvent.id,
                profiles: profilesToCharge,
                buyerEmail: userEmail,
                buyerName: userName,
                buyerUserId: buyerUserId ? parseInt(buyerUserId) : null,
                successPath: '/bilheteria',
                cancelPath: '/bilheteria',
                queryParams: `?success=true&eventId=${selectedEvent.id}`,
            }
        });
    };


    const modalidades = ['Todas', ...new Set(events.map(match => match.modalidade))];

    const filteredMatches = events.filter(match =>
        selectedModalidade === 'Todas' || match.modalidade === selectedModalidade
    );

    const getDisplayPrice = (event) => {
        if (!isLoggedIn) {
            let lowest = 0;
            if (event.ticketPriceSocio !== null && event.ticketPriceNonSocio !== null) {
                lowest = Math.min(event.ticketPriceSocio, event.ticketPriceNonSocio);
            } else if (event.ticketPriceSocio !== null) lowest = event.ticketPriceSocio;
            else if (event.ticketPriceNonSocio !== null) lowest = event.ticketPriceNonSocio;

            if (lowest === 0 && event.ticketPriceSocio === null && event.ticketPriceNonSocio === null) {
                return 'Grátis';
            }
            return `${lowest}€`;
        }

        const price = isSocio ? (event.ticketPriceSocio ?? 0) : (event.ticketPriceNonSocio ?? 0);
        return `${price}€`;
    };

    const getTicketPrice = (event, profile = null, sport = null) => {
        if (event.isAnnual) {
            if (!sport) return 40; // Fallback
            const isMember = profile ? (profile.isSocio || profile.dashboardType?.toLowerCase() === 'atleta') : isSocio;
            const hasLate = profile ? profile.hasLateQuotas : (() => {
                const currentUserId = localStorage.getItem('userId');
                const currentUserProfile = linkedUsers.find(lu => lu.id.toString() === currentUserId?.toString());
                return currentUserProfile?.hasLateQuotas || false;
            })();
            const isEffectiveMember = isMember && !hasLate;
            return isEffectiveMember ? (sport.annualTicketPriceSocio ?? 40) : (sport.annualTicketPriceNonSocio ?? 40);
        }
        if (!isLoggedIn) return 0;

        // Se um perfil específico for fornecido, verificar o seu estatuto
        if (profile) {
            // Usa o flag isSocio vindo do backend, ou fallback para atleta (obrigatório ser sócio)
            const isMember = profile.isSocio || profile.dashboardType?.toLowerCase() === 'atleta';
            const hasLate = profile.hasLateQuotas;
            const isEffectiveMember = isMember && !hasLate;
            return isEffectiveMember ? (event.ticketPriceSocio ?? 0) : (event.ticketPriceNonSocio ?? 0);
        }

        // Caso contrário, usa o estatuto do utilizador atual
        const currentUserId = localStorage.getItem('userId');
        const currentUserProfile = linkedUsers.find(lu => lu.id.toString() === currentUserId?.toString());
        const hasLate = currentUserProfile?.hasLateQuotas;
        const isEffectiveMember = isSocio && !hasLate;
        return isEffectiveMember ? (event.ticketPriceSocio ?? 0) : (event.ticketPriceNonSocio ?? 0);
    };

    const calculateTotal = () => {
        if (!selectedEvent) return 0;
        if (isAnnualTicketPurchase) {
            let total = 0;
            selectedProfiles.forEach(p => {
                selectedAnnualSports.forEach(s => {
                    total += getTicketPrice(selectedEvent, p, s);
                });
            });
            return total;
        }
        if (selectedProfiles.length > 0) {
            return selectedProfiles.reduce((sum, p) => sum + getTicketPrice(selectedEvent, p), 0);
        }
        return getTicketPrice(selectedEvent) * quantity;
    };

    if (loading) {
        return (
            <div className="bilheteria-page">
                <div className="bilheteria-hero">
                    <div className="hero-content">
                        <img src="/CDP_logo.png" alt="CDP Logo" className="hero-logo" />
                    </div>
                </div>
                <div className="bilheteria-container" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '40vh' }}>
                    <div className="spinner"></div>
                    <p style={{ marginTop: '20px', color: '#4a5568', fontSize: '1.2rem' }}>A carregar bilheteira...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="bilheteria-page">
            {/* Purchase Modal */}
            {showPurchaseModal && selectedEvent && (
                <div className="purchase-modal-overlay" onClick={() => setShowPurchaseModal(false)}>
                    <div className="purchase-modal" onClick={e => e.stopPropagation()}>
                        <button className="close-modal" onClick={() => setShowPurchaseModal(false)}>×</button>

                        {purchaseStep === 'sport-selection' && (
                            <>
                                <div className="purchase-header">
                                    <FaTicketAlt className="main-icon" />
                                    <h2>Quais as modalidades?</h2>
                                    <p>Seleciona as modalidades para o teu bilhete anual.</p>
                                </div>
                                <div className="profile-selection-grid">
                                    {allSports.map(sport => {
                                        const isSelected = selectedAnnualSports.find(s => s.id === sport.id);
                                        return (
                                            <button
                                                key={sport.id}
                                                className={`profile-select-btn sport-select ${isSelected ? 'active' : ''}`}
                                                onClick={() => {
                                                    if (isSelected) {
                                                        setSelectedAnnualSports(selectedAnnualSports.filter(s => s.id !== sport.id));
                                                    } else {
                                                        setSelectedAnnualSports([...selectedAnnualSports, sport]);
                                                    }
                                                }}
                                            >
                                                <div className="profile-avatar-small sport-icon-placeholder">
                                                    {sport.name[0]}
                                                </div>
                                                <div className="profile-btn-info">
                                                    <span className="profile-name">{sport.name}</span>
                                                    <span className="sport-prices">
                                                        Sócio: {sport.annualTicketPriceSocio ?? 40}€ | Geral: {sport.annualTicketPriceNonSocio ?? 40}€
                                                    </span>
                                                </div>
                                                {isSelected && <FaCheckCircle className="selection-check" />}
                                            </button>
                                        );
                                    })}
                                </div>
                                <div className="selection-footer">
                                    <button
                                        className="btn-confirm"
                                        disabled={selectedAnnualSports.length === 0}
                                        onClick={() => {
                                            if (linkedUsers.length > 1) {
                                                setPurchaseStep('selection');
                                            } else {
                                                const defaultProfile = linkedUsers[0] || null;
                                                setSelectedProfiles(defaultProfile ? [defaultProfile] : []);
                                                setPurchaseStep('summary');
                                            }
                                        }}
                                    >
                                        Continuar ({selectedAnnualSports.length} Modalidades)
                                    </button>
                                </div>
                            </>
                        )}

                        {purchaseStep === 'selection' && (
                            <>
                                <div className="purchase-header">
                                    <FaUser className="main-icon" />
                                    <h2>Para quem é o bilhete?</h2>
                                    <p>Podes selecionar várias pessoas da tua família.</p>
                                    {linkedUsers.some(lu => lu.hasLateQuotas) && (
                                        <p style={{ color: '#ef4444', fontSize: '0.85rem', fontWeight: 'bold', marginTop: '8px' }}>
                                            ⚠️ Atenção: Perfis com quotas em atraso pagarão a tarifa Geral de Não-Sócio.
                                        </p>
                                    )}
                                </div>
                                <div className="profile-selection-grid">
                                    {linkedUsers.map(profile => {
                                        const isSelected = selectedProfiles.find(p => p.id === profile.id);
                                        return (
                                            <button
                                                key={profile.id}
                                                className={`profile-select-btn ${isSelected ? 'active' : ''} ${profile.hasLateQuotas ? 'has-late-quotas' : ''}`}
                                                onClick={() => {
                                                    if (isSelected) {
                                                        setSelectedProfiles(selectedProfiles.filter(p => p.id !== profile.id));
                                                    } else {
                                                        setSelectedProfiles([...selectedProfiles, profile]);
                                                    }
                                                }}
                                            >
                                                <div className="profile-avatar-small">
                                                    {profile.firstName[0]}{profile.lastName[0]}
                                                </div>
                                                <div className="profile-btn-info">
                                                    <span className="profile-name">{profile.firstName} {profile.lastName}</span>
                                                    {profile.hasLateQuotas && (
                                                        <span className="profile-quota-warning-badge">Quotas em Atraso</span>
                                                    )}
                                                </div>
                                                {isSelected && <FaCheckCircle className="selection-check" />}
                                            </button>
                                        );
                                    })}
                                </div>
                                <div className="selection-footer">
                                    <button
                                        className="btn-confirm"
                                        disabled={selectedProfiles.length === 0}
                                        onClick={() => {
                                            setQuantity(selectedProfiles.length);
                                            setPurchaseStep('summary');
                                        }}
                                    >
                                        Continuar ({selectedProfiles.length})
                                    </button>
                                </div>
                            </>
                        )}

                        {purchaseStep === 'summary' && (
                            <>
                                <div className="purchase-header">
                                    <FaTicketAlt className="main-icon" />
                                    <h2>Confirmar Compra</h2>
                                    <p>Estás a um passo de apoiar o CDP!</p>
                                </div>
                                <div className="purchase-details">
                                    <div className="selected-profile-summary multi">
                                        <span className="label">Bilhete para:</span>
                                        <div className="selected-names-list">
                                            {selectedProfiles.map((p, i) => (
                                                <span key={p.id} className="name-tag">
                                                    {p.firstName}{i < selectedProfiles.length - 1 ? ',' : ''}
                                                </span>
                                            ))}
                                        </div>
                                        {linkedUsers.length > 1 && (
                                            <button className="btn-change-profile" onClick={() => setPurchaseStep('selection')}>Alterar</button>
                                        )}
                                    </div>
                                    <div className="match-summary">
                                        <h3>{selectedEvent.homeTeam} VS {selectedEvent.awayTeam}</h3>
                                        <p><FaCalendarAlt /> {selectedEvent.date} às {selectedEvent.time}</p>
                                        <p><FaMapMarkerAlt /> {selectedEvent.location}</p>
                                    </div>
                                    <div className="price-breakdown">
                                        {selectedProfiles.map(p => {
                                            const isMember = p.isSocio || p.dashboardType?.toLowerCase() === 'atleta';
                                            const hasLate = p.hasLateQuotas;
                                            const isEffectiveMember = isMember && !hasLate;

                                            if (isAnnualTicketPurchase) {
                                                return selectedAnnualSports.map(s => {
                                                    const price = getTicketPrice(selectedEvent, p, s);
                                                    return (
                                                        <div key={`${p.id}-${s.id}`} className="price-row item" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '4px' }}>
                                                            <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                                                                <span>{p.firstName} ({s.name}): {price}€</span>
                                                                <span className="profile-badge-small">{isEffectiveMember ? 'Sócio' : 'Geral'}</span>
                                                            </div>
                                                            {hasLate && isMember && (
                                                                <div className="checkout-quota-warning">
                                                                    ⚠️ Quotas em atraso: aplicada tarifa Geral (Não-Sócio)
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                });
                                            }

                                            const price = getTicketPrice(selectedEvent, p);
                                            return (
                                                <div key={p.id} className="price-row item" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '4px' }}>
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                                                        <span>{p.firstName}: {price}€</span>
                                                        <span className="profile-badge-small">{isEffectiveMember ? 'Sócio' : 'Geral'}</span>
                                                    </div>
                                                    {hasLate && isMember && (
                                                        <div className="checkout-quota-warning">
                                                            ⚠️ Quotas em atraso: aplicada tarifa Geral (Não-Sócio)
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}

                                        {!isAnnualTicketPurchase && selectedProfiles.length === 1 && (
                                            <div className="quantity-selector">
                                                <span>Quantidade Extra</span>
                                                <div className="quantity-controls">
                                                    <button
                                                        className="qty-btn"
                                                        onClick={() => setQuantity(Math.max(1, quantity - 1))}
                                                        disabled={quantity <= 1}
                                                    >−</button>
                                                    <span className="qty-value">{quantity}</span>
                                                    <button
                                                        className="qty-btn"
                                                        onClick={() => setQuantity(Math.min(10, quantity + 1))}
                                                        disabled={quantity >= 10}
                                                    >+</button>
                                                </div>
                                            </div>
                                        )}

                                        <div className="price-row total">
                                            <span>Total a Pagar</span>
                                            <span>{calculateTotal().toFixed(2)}€</span>
                                        </div>
                                    </div>
                                </div>
                                <button className="btn-confirm" onClick={confirmPurchase}>Confirmar e Reservar</button>
                                <p className="secure-notice"><FaInfoCircle /> Pagamento processado de forma segura.</p>
                            </>
                        )}

                        {purchaseStep === 'processing' && (
                            <div className="purchase-loading">
                                <div className="spinner"></div>
                                <h3>{isAnnualTicketPurchase ? 'A processar o teu Bilhete Anual...' : 'A processar o teu bilhete...'}</h3>
                                <p>Por favor, não feches esta janela.</p>
                            </div>
                        )}

                        {purchaseStep === 'success' && (
                            <div className="purchase-success">
                                <FaCheckCircle className="success-icon" />
                                <h3>{isAnnualTicketPurchase ? 'Bilhete Anual Adquirido!' : 'Compra Concluída!'}</h3>
                                <p>
                                    {isAnnualTicketPurchase
                                        ? `O Bilhete Anual para ${selectedProfiles.map(p => p.firstName).join(', ')} (época 2025/2026) foi processado com sucesso. Bem-vindo à família!`
                                        : `O bilhete para ${selectedProfiles.map(p => p.firstName).join(', ')} para o jogo ${selectedEvent.awayTeam === 'CD Póvoa' ? selectedEvent.homeTeam : selectedEvent.awayTeam} foi reservado com sucesso.`
                                    }
                                </p>
                                <div className="success-actions">
                                    <button className="btn-primary" onClick={() => setShowPurchaseModal(false)}>Fechar</button>
                                    <button className="btn-outline" onClick={() => window.print()}>Imprimir {isAnnualTicketPurchase ? 'Comprovativo' : 'Bilhete'}</button>
                                </div>
                            </div>
                        )}

                        {purchaseStep === 'pending' && (
                            <div className="purchase-success" style={{ textAlign: 'center' }}>
                                <FaClock className="success-icon" style={{ color: '#f59e0b' }} />
                                <h3 style={{ color: '#d97706' }}>Estado do Pagamento</h3>
                                <p>
                                    <span style={{ fontSize: '0.9em', display: 'block', marginTop: '10px', color: '#6b7280' }}>
                                        Se recusaste o pagamento ou ocorreu algum erro, podes fechar esta janela e tentar novamente.
                                    </span>
                                </p>
                                <div className="success-actions">
                                    <button className="btn-primary" onClick={() => setShowPurchaseModal(false)}>Fechar</button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Annual Ticket Info Modal */}
            {showAnnualInfoModal && (
                <div className="purchase-modal-overlay" onClick={() => setShowAnnualInfoModal(false)}>
                    <div className="purchase-modal annual-modal" onClick={e => e.stopPropagation()}>
                        <button className="close-modal" onClick={() => setShowAnnualInfoModal(false)}>×</button>

                        <div className="purchase-header">
                            <FaTicketAlt className="main-icon" />
                            <h2>Bilhete Anual 2025/2026</h2>
                            <p>Tudo o que precisas de saber</p>
                        </div>

                        <div className="annual-modal-content">
                            <div className="benefits-section">
                                <h3>Vantagens Exclusivas</h3>
                                <ul>
                                    <li><FaCheckCircle className="check-icon" /> Acesso a todos os jogos em casa </li>
                                    <li><FaCheckCircle className="check-icon" /> Lugar marcado personalizado no pavilhão</li>
                                    <li><FaCheckCircle className="check-icon" /> Prioridade na aquisição de bilhetes para fases finais</li>
                                </ul>
                            </div>
                        </div>

                        <button className="btn-confirm" onClick={handleAnnualTicketBuy}>Comprar Agora</button>
                        <p className="secure-notice"><FaInfoCircle /> Disponível para sócios e não sócios.</p>
                    </div>
                </div>
            )}

            <section className="bilheteria-hero">
                <div className="hero-content">
                    <img src="/CDP_logo.png" alt="CDP Logo" className="hero-logo" />
                    <h1>Bilheteira Oficial</h1>
                    <p>Garante o teu lugar e apoia as nossas equipas em mais uma época de glória.</p>
                </div>
            </section>

            <div className="bilheteria-container">
                {/* Season Tickets Promo */}
                <section className="season-tickets-promo">
                    <div className="promo-card">
                        <div className="promo-icon"><FaTicketAlt /></div>
                        <h2>Lugar Anual 25/26</h2>
                        <p>Junta-te à família CDP e garante o teu lugar para todos os jogos em casa com vantagens exclusivas.</p>
                        <button className="bilhete-btn-primary" onClick={() => setShowAnnualInfoModal(true)}>
                            Saber Mais & Comprar <FaTicketAlt style={{ marginLeft: '10px', fontSize: '0.9rem' }} />
                        </button>
                    </div>
                </section>

                {/* Upcoming Matches */}
                <section className="upcoming-matches">
                    <div className="section-title-wrapper">
                        <span className="section-subtitle">Próximos Confrontos</span>
                        <h2>Bilhetes Disponíveis</h2>
                    </div>

                    <div className="filters-container">
                        {modalidades.map((mod) => (
                            <button
                                key={mod}
                                className={`filter-btn ${selectedModalidade === mod ? 'active' : ''}`}
                                onClick={() => setSelectedModalidade(mod)}
                            >
                                {mod}
                            </button>
                        ))}
                    </div>

                    <div className="matches-grid">
                        {filteredMatches.length > 0 ? (
                            filteredMatches.map((match) => (
                                <div className="match-card" key={match.id}>
                                    {match.status === 'selling-fast' && (
                                        <div className="match-status-badge">Últimos Bilhetes!</div>
                                    )}
                                    <span className="match-competition">{match.modalidade} | {match.competition}</span>
                                    <div className="match-teams">
                                        <div className="team">
                                            <span className="team-name">{match.homeTeam}</span>
                                        </div>
                                        <div className="vs-circle">VS</div>
                                        <div className="team">
                                            <span className="team-name">{match.awayTeam}</span>
                                        </div>
                                    </div>

                                    <div className="match-details">
                                        <div className="detail-item">
                                            <FaCalendarAlt className="detail-icon" />
                                            <span>{match.date} às {match.time}</span>
                                        </div>
                                        <div className="detail-item">
                                            <FaMapMarkerAlt className="detail-icon" />
                                            <span>{match.location}</span>
                                        </div>
                                    </div>

                                    <div className="match-footer">
                                        {match.hasTicketing ? (
                                            <>
                                                <div className="price-box">
                                                    <span className="price-label">A partir de</span>
                                                    <span className="price-value">{getDisplayPrice(match)}</span>
                                                </div>
                                                <button
                                                    className={`btn-buy ${match.status === 'sold-out' ? 'disabled' : ''}`}
                                                    onClick={() => handleBuyClick(match)}
                                                    disabled={match.status === 'sold-out'}
                                                >
                                                    {match.status === 'sold-out' ? 'Esgotado' : 'Comprar Bilhete'}
                                                </button>
                                            </>
                                        ) : (
                                            <div className="match-free-entry">
                                                <FaInfoCircle className="detail-icon" />
                                                <span>Entrada Livre</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="no-matches" style={{ textAlign: 'center', padding: '80px 0', color: 'var(--text-muted)' }}>
                                <p style={{ fontSize: '1.2rem', fontWeight: '300' }}>Não existem jogos disponíveis para esta modalidade de momento.</p>
                            </div>
                        )}
                    </div>
                </section>

                <section className="bilheteria-info">
                    <div className="info-box">
                        <h3>Informações Importantes</h3>
                        <ul>
                            <li>Os bilhetes online podem ser apresentados no telemóvel (não é necessário imprimir).</li>
                            <li>Abertura de portas: 1 hora antes do início do jogo.</li>
                            <li>Sócios têm desconto exclusivo na compra de bilhetes e prioridade na aquisição.</li>
                            <li>Crianças até 3 anos (inclusive) não pagam bilhete, desde que acompanhadas por um adulto e não ocupem lugar sentado.</li>
                            <li>O Bilhete Anual é nominal e intransmissível, válido apenas para a época corrente.</li>
                            <li>Garante o teu bilhete com antecedência para evitar filas no pavilhão.</li>
                        </ul>
                    </div>
                </section>
            </div>
        </div>
    );
};

export default Bilheteria;
